import { Controller } from '@hotwired/stimulus'
import { t } from '@lingui/core/macro'

export default class extends Controller {
  static targets = ['container', 'hiddenInput']
  static values = { initialData: String, awsAccounts: Array }

  connect() {
    this.form = this.element.closest('form')
    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit.bind(this))
    }

    this.hiddenInput = this.findHiddenInput()
    if (this.hiddenInput) {
      this.hiddenInput.value = '[]'
    }

    let initialData = []
    if (this.hasInitialDataValue && this.initialDataValue) {
      try {
        const parsed = JSON.parse(this.initialDataValue)
        initialData = Array.isArray(parsed) ? parsed : []
      } catch (_e) {
        initialData = []
      }
    }

    if (initialData.length > 0) {
      initialData.forEach((item) => {
        this.addFilterFields(item)
      })
      this.updateRemoveButtons()
      this.updateHiddenInput()
    }
  }

  disconnect() {
    if (this.form) {
      this.form.removeEventListener('submit', this.handleSubmit.bind(this))
    }
  }

  handleSubmit(_event) {
    this.updateHiddenInput()
  }

  findHiddenInput() {
    if (this.hasHiddenInputTarget) {
      return this.hiddenInputTarget
    }
    const form = this.element.closest('form')
    if (form) {
      return form.querySelector('input[name*="account_level_info"]')
    }
    return null
  }

  addFilterFields(data = null) {
    const index = this.containerTarget.children.length
    const filterFields = this.createFilterFields(index, data)
    this.containerTarget.appendChild(filterFields)
    this.updateHiddenInput()
    this.updateRemoveButtons()
  }

  removeFilterFields(event) {
    const filterGroup = event.currentTarget.closest('.billing-v2-tag-filter-group')
    const groups = this.containerTarget.querySelectorAll('.billing-v2-tag-filter-group')

    if (groups.length >= 1) {
      const tomSelectElement = filterGroup.querySelector('[data-controller*="tom-select"]')
      if (tomSelectElement) {
        const tomSelectController = this.application.getControllerForElementAndIdentifier(
          tomSelectElement,
          'tom-select',
        )
        if (tomSelectController?.tomSelectInstance) {
          tomSelectController.tomSelectInstance.destroy()
        }
      }
      filterGroup.remove()
      this.updateHiddenInput()
      this.updateRemoveButtons()
    }
  }

  updateRemoveButtons() {
    const removeButtons = this.containerTarget.querySelectorAll('[data-action*="removeFilterFields"]')
    removeButtons.forEach((button) => {
      button.disabled = false
    })
  }

  updateHiddenInput() {
    const filters = this.collectFilterData()

    if (!this.hiddenInput) {
      this.hiddenInput = this.findHiddenInput()
    }

    if (this.hiddenInput) {
      this.hiddenInput.value = JSON.stringify(filters)
    }
  }

  collectFilterData() {
    const filters = []
    const groups = this.containerTarget.querySelectorAll('.billing-v2-tag-filter-group')

    groups.forEach((group) => {
      const awsAccountIdInput = group.querySelector('[data-field="aws_account_id"]')
      const tagKeyInput = group.querySelector('[data-field="tag_key"]')
      const tagValuesInput = group.querySelector('[data-field="tag_values"]')

      if (!awsAccountIdInput || !tagKeyInput || !tagValuesInput) return

      const awsAccountId = awsAccountIdInput.value.trim()
      if (!awsAccountId) return

      const tagKey = tagKeyInput.value.trim()
      const rawTagValues = tagValuesInput.value.trim()
      const tagValues = rawTagValues
        ? rawTagValues
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean)
        : []

      filters.push({ aws_account_id: awsAccountId, tag_key: tagKey, tag_values: tagValues })
    })

    return filters
  }

  createFilterFields(index, data = null) {
    const group = document.createElement('div')
    group.className = 'billing-v2-tag-filter-group mb-3 p-3 border rounded'
    group.dataset.index = index

    const awsAccountId = data?.aws_account_id ? String(data.aws_account_id) : ''
    const tagKey = data?.tag_key ? String(data.tag_key) : ''
    const tagValues = Array.isArray(data?.tag_values) ? data.tag_values.join(', ') : ''

    const accountsOptions = this.awsAccountsValue
      .map(([label, value]) => {
        const selected = value === awsAccountId ? 'selected' : ''
        return `<option value="${this.escapeHtml(value)}" ${selected}>${this.escapeHtml(label)}</option>`
      })
      .join('')

    group.innerHTML = `
      <div class="row">
        <div class="col-md-4">
          <label class="form-label">${t`AWS Account`}</label>
          <select
            class="form-select"
            data-field="aws_account_id"
            data-controller="tom-select"
            data-tom-select-placeholder-value="${t`Select account`}">
            <option value="">${t`Select account`}</option>
            ${accountsOptions}
          </select>
        </div>
        <div class="col-md-3">
          <label class="form-label">${t`Tag Key`}</label>
          <input
            type="text"
            class="form-control"
            data-field="tag_key"
            value="${this.escapeHtml(tagKey)}"
            placeholder="${t`e.g. Env`}"
          />
        </div>
        <div class="col-md-4">
          <label class="form-label">${t`Tag Values`}</label>
          <input
            type="text"
            class="form-control"
            data-field="tag_values"
            value="${this.escapeHtml(tagValues)}"
            placeholder="${t`prod, staging`}"
          />
          <div class="form-text">${t`Comma-separated values`}</div>
        </div>
        <div class="col-md-1 d-flex align-items-center justify-content-center">
          <button
            type="button"
            class="btn btn-outline-danger"
            data-action="click->billing-v2-account-tag-filters#removeFilterFields"
            title="${t`Remove`}">
            &times;
          </button>
        </div>
      </div>
    `

    const inputs = group.querySelectorAll('input[data-field], select[data-field]')
    inputs.forEach((input) => {
      input.addEventListener('input', () => this.updateHiddenInput())
      input.addEventListener('change', () => this.updateHiddenInput())
    })

    return group
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = String(text)
    return div.innerHTML
  }
}
